import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Clock, Package, ShoppingCart, Sparkles, Sword, Ticket } from 'lucide-react';
import { authApi, shopApi } from '@/api';
import type { BuyResult, Product, ProductKind } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { BuyDialog } from '@/components/BuyDialog';
import { PurchaseSuccessDialog } from '@/components/PurchaseSuccessDialog';
import { formatRub } from '@/lib/utils';
import { PRODUCT_KIND, ruLabel } from '@/lib/labels';

const KIND_ICON: Record<ProductKind, typeof Ticket> = {
  pass: Ticket,
  role: Sparkles,
  item: Sword,
  bundle: Package,
  custom: ShoppingCart,
};

function durationLabel(sec: number | null): string | null {
  if (!sec) return null;
  if (sec >= 86_400 * 28) return `${Math.round(sec / 86_400 / 30)} мес.`;
  if (sec >= 86_400) return `${Math.round(sec / 86_400)} дн.`;
  if (sec >= 3600) return `${Math.round(sec / 3600)} ч.`;
  return `${Math.round(sec / 60)} мин.`;
}

export function ShopPage() {
  // Фильтр храним как slug категории; список вкладок приходит с бэка.
  const [filter, setFilter] = useState<string>('all');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: authApi.me, retry: false });
  const { data: categories } = useQuery({
    queryKey: ['shop', 'categories'],
    queryFn: shopApi.categories,
  });
  const { data: products, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => shopApi.products(),
  });

  const [dialogProduct, setDialogProduct] = useState<Product | null>(null);
  const [successResult, setSuccessResult] = useState<BuyResult | null>(null);

  function handleBuyClick(p: Product) {
    if (!me) {
      window.location.href = authApi.loginUrl();
      return;
    }
    if (!me.minecraftNickname) {
      toast.error('Сначала привяжите никнейм в профиле');
      navigate('/profile');
      return;
    }
    setDialogProduct(p);
  }

  // Маппинг slug → id для эффективной фильтрации (categories загружается отдельно).
  const tabs: Array<{ value: string; label: string }> = (categories ?? []).map((c) => ({
    value: c.slug,
    label: c.name,
  }));
  const activeFilter = tabs.some((t) => t.value === filter)
    ? filter
    : tabs[0]?.value ?? 'all';

  const activeCategoryId =
    activeFilter === 'all' ? null : categories?.find((c) => c.slug === activeFilter)?.id ?? null;
  const filtered = (products ?? []).filter(
    (p) => activeFilter === 'all' || (activeCategoryId !== null && p.categoryId === activeCategoryId),
  );

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-12 py-12">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <h1 className="text-3xl md:text-4xl font-extrabold text-eol-white">Магазин</h1>
        {me && (
          <p className="text-eol-desc">
            Ваш баланс:{' '}
            <span className="text-eol-pink font-bold">{formatRub(me.balance)}</span>
          </p>
        )}
      </div>

      <div className="mt-6 flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <button
            key={t.value}
            onClick={() => setFilter(t.value)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${
              activeFilter === t.value
                ? 'bg-eol-pink/20 text-eol-pink ring-1 ring-eol-pink/40'
                : 'bg-white/5 text-eol-desc hover:text-eol-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {me && !me.minecraftNickname && (
        <div className="mt-6 rounded-lg border border-eol-pink/40 bg-eol-pink/10 px-4 py-3 text-sm">
          <span className="text-eol-white font-semibold">Привяжите никнейм</span>{' '}
          <span className="text-eol-desc">- без него покупки нельзя доставить на сервер.</span>{' '}
          <button onClick={() => navigate('/profile')} className="text-eol-pink underline font-semibold">
            Перейти в профиль
          </button>
        </div>
      )}

      {isLoading ? (
        <p className="text-eol-desc mt-10">Загрузка…</p>
      ) : filtered.length === 0 ? (
        <p className="text-eol-desc mt-10">Товаров пока нет.</p>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              isAuthed={Boolean(me)}
              hasNickname={Boolean(me?.minecraftNickname)}
              onBuy={() => handleBuyClick(p)}
            />
          ))}
        </div>
      )}

      {dialogProduct && (
        <BuyDialog
          product={dialogProduct}
          open
          onClose={() => setDialogProduct(null)}
          userBalanceMinor={me?.balance ?? 0}
          onSuccess={(r) => {
            setDialogProduct(null);
            setSuccessResult(r);
            queryClient.invalidateQueries({ queryKey: ['me'] });
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['passes'] });
            queryClient.invalidateQueries({ queryKey: ['inventory'] });
          }}
        />
      )}

      <PurchaseSuccessDialog
        result={successResult}
        onClose={() => setSuccessResult(null)}
      />
    </div>
  );
}

function ProductCard({
  product,
  isAuthed,
  hasNickname,
  onBuy,
}: {
  product: Product;
  isAuthed: boolean;
  hasNickname: boolean;
  onBuy: () => void;
}) {
  const Icon = KIND_ICON[product.kind];

  // Бейдж длительности: если есть тарифы, показываем самый короткий (для tier.length === 1
  // это и есть единственный срок); если нет - durationSec самого товара; иначе ничего.
  const tiers = product.durations ?? [];
  const shortest =
    tiers.length > 0
      ? [...tiers].sort((a, b) => (a.days || Infinity) - (b.days || Infinity))[0]
      : null;
  const durationText = shortest
    ? (shortest.days === 0 ? 'Навсегда' : durationLabel(shortest.days * 86_400))
    : durationLabel(product.durationSec);
  const hasMultiple = tiers.length > 1;

  // Цена: если есть тарифы - минимальная из них (как «от»), иначе priceMinor.
  const minPrice = tiers.length > 0
    ? Math.min(...tiers.map((t) => t.priceMinor))
    : product.priceMinor;

  return (
    <Card className="flex flex-col overflow-hidden hover:border-eol-pink/30 transition">
      {product.imageUrl ? (
        <div
          className="aspect-[16/9] bg-cover bg-center"
          style={{ backgroundImage: `url(${product.imageUrl})` }}
        />
      ) : (
        <div className="aspect-[16/9] flex items-center justify-center bg-gradient-to-br from-eol-pink/10 to-transparent">
          <Icon className="h-12 w-12 text-eol-pink/60" />
        </div>
      )}
      <CardContent className="flex-1 flex flex-col p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="secondary">{ruLabel(PRODUCT_KIND, product.kind)}</Badge>
          {durationText && (
            <Badge variant="outline" className="gap-1">
              <Clock className="h-3 w-3" />
              {durationText}
              {hasMultiple && <span className="text-eol-desc/60 ml-1">и др.</span>}
            </Badge>
          )}
        </div>
        <h3 className="font-bold text-lg text-eol-white">{product.name}</h3>
        {product.description && (
          <p className="mt-2 text-sm text-eol-desc leading-relaxed whitespace-pre-line flex-1">{product.description}</p>
        )}

        {product.items && product.items.length > 0 && (
          <ul className="mt-3 text-sm text-eol-desc space-y-1">
            {product.items.map((it) => (
              <li key={it.id} className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-eol-pink" />
                {it.name}
                {it.quantity > 1 && (
                  <span className="text-eol-desc/60">×{it.quantity}</span>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          <div>
            {product.oldPriceMinor && product.oldPriceMinor > minPrice && (
              <p className="text-sm text-eol-desc line-through">
                {formatRub(product.oldPriceMinor)}
              </p>
            )}
            <p className="text-xl font-extrabold text-eol-pink">
              {hasMultiple && <span className="text-sm text-eol-desc font-normal mr-1">от</span>}
              {formatRub(minPrice)}
            </p>
          </div>
          <Button
            onClick={onBuy}
            title={
              !isAuthed
                ? 'Войдите через Discord'
                : !hasNickname
                  ? 'Привяжите никнейм в профиле'
                  : ''
            }
          >
            Купить
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
